
class ModalStackManager {
  constructor() {
    this.stack = [];
    this.initialized = false;
    this.pendingCleanup = null;
    this.historyDepth = 0;
    // Prevents cleanup-triggered popstate events from closing newly opened modals.
    this.suppressPopstate = false;
    this.suppressTimeout = null;
    this.initialPathname = null;
    // Full pathname+search captured when the stack first becomes non-empty. Used to detect
    // a legitimate URL change (e.g. a filter Apply button doing navigate(url, {replace:true})
    // into the pushed history entry) made while the modal was open, so cleanup doesn't blow
    // it away with history.go(-N).
    this.initialHref = null;
  }

  initialize() {
    if (this.initialized) {
      return;
    }

    window.addEventListener('popstate', (event) => {
      if (this.suppressPopstate) {
        // Tag event so downstream listeners know this popstate was programmatic cleanup
        event.__sahiModalHandled = true;
        return;
      }

      if (this.stack.length > 0) {
        // Tag event so downstream listeners (e.g. Main.js) know this back press was consumed by a modal
        event.__sahiModalHandled = true;

        const topModal = this.stack.pop();
        if (this.historyDepth > 0) {
          this.historyDepth--;
        }
        if (topModal) {
          topModal.poppedViaBack = true;
          if (topModal.onClose) {
            topModal.onClose();
          }
        }
      }
    });

    this.initialized = true;
  }

  push(id, onClose) {
    // Cancel any pending history cleanup — a new modal is opening
    if (this.pendingCleanup) {
      clearTimeout(this.pendingCleanup);
      this.pendingCleanup = null;
    }

    if (this.stack.length === 0) {
      this.initialPathname = window.location.pathname;
      this.initialHref = window.location.pathname + window.location.search;
    }

    this.stack.push({ id, onClose, pushed: true });

    // Preserve existing React Router v6 state (idx, key, usr) so routing doesn't get corrupted
    const currentState = (window.history.state && typeof window.history.state === 'object')
      ? window.history.state
      : {};

    window.history.pushState(
      {
        ...currentState,
        modal: true,
        __sahiModal: true,
        modalId: id
      },
      '',
      window.location.href
    );
    this.historyDepth++;
  }

  remove(id, viaBackButton = false) {
    const index = this.stack.findIndex(m => m.id === id);
    let item = null;
    if (index !== -1) {
      item = this.stack.splice(index, 1)[0];
    }

    if (viaBackButton) {
      // If item was still in stack (not yet popped by popstate) and pushed state, decrement historyDepth
      if (item && !item.poppedViaBack && this.historyDepth > 0) {
        this.historyDepth--;
      }
    } else if (item && item.pushed) {
      // Modal closed via UI (Cancel, Save, Backdrop)
      // If there are still parent modals in the stack, unwind this child modal's entry immediately
      if (this.stack.length > 0 && this.historyDepth > 0) {
        this.historyDepth--;
        this.suppressPopstateSafely();
        window.history.go(-1);
      }
    }
  }

  suppressPopstateSafely() {
    if (this.suppressTimeout) {
      clearTimeout(this.suppressTimeout);
    }
    this.suppressPopstate = true;

    const clearSuppression = () => {
      this.suppressPopstate = false;
      if (this.suppressTimeout) {
        clearTimeout(this.suppressTimeout);
        this.suppressTimeout = null;
      }
      window.removeEventListener('popstate', clearSuppression);
    };

    // Browsers fire only ONE popstate event for history.go(-N)
    window.addEventListener('popstate', clearSuppression, { once: true });
    // Safety fallback: guaranteed unlock after 150ms
    this.suppressTimeout = setTimeout(clearSuppression, 150);
  }

  scheduleCleanup() {
    if (this.pendingCleanup) {
      clearTimeout(this.pendingCleanup);
    }

    this.pendingCleanup = setTimeout(() => {
      this.pendingCleanup = null;

      if (this.stack.length === 0 && this.historyDepth > 0) {
        const currentHref = window.location.pathname + window.location.search;
        // Bail if the pathname changed (navigated away) OR the URL was rewritten in place
        // (e.g. a filter Apply button did navigate(url, {replace:true}) while the modal was
        // open) - popping history here would silently discard that legitimate change.
        if (this.initialPathname && window.location.pathname !== this.initialPathname) {
          this.historyDepth = 0;
          this.initialPathname = null;
          this.initialHref = null;
          return;
        }
        if (this.initialHref && currentHref !== this.initialHref) {
          this.historyDepth = 0;
          this.initialPathname = null;
          this.initialHref = null;
          return;
        }

        const timesToGoBack = this.historyDepth;
        this.historyDepth = 0;
        this.initialPathname = null;
        this.initialHref = null;

        // Suppress popstate safely with single-shot listener + safety timeout guard
        this.suppressPopstateSafely();
        window.history.go(-timesToGoBack);
      }
    }, 150);
  }

  hasOpenModals() {
    return this.stack.length > 0;
  }

  isCleaningUp() {
    return this.suppressPopstate || this.pendingCleanup !== null;
  }

  getState() {
    return {
      stackLength: this.stack.length,
      historyDepth: this.historyDepth,
      stack: this.stack.map(s => ({ id: s.id }))
    };
  }
}

export const modalStackManager = new ModalStackManager();
